import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useWistorAuth } from '../../api/auth'
import {
  useProject, useProjects,
  useRenameApp, useMoveApp, useToggleAppPublic, useDeleteApp,
} from '../shared/use-v2-queries'
import { useApiClient } from '../shared/api-client'
import { useApiBase } from '../shared/api-base-provider'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import orderBy from 'lodash/orderBy'
// No longer importing JsonEditor — using styled <pre> for consistent v2 theme

// ─── Types ────────────────────────────────────────────
type AppItem = {
  id: string
  title: string
  path: string
  public: boolean
  noConfigCache?: boolean
  deleted?: string | null
  createdAt?: string
  user?: { id: string; name: string } | null
}

// ─── Icons ────────────────────────────────────────────
const ic = {
  search: 'M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z',
  plus: 'M12 5v14M5 12h14',
  edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  move: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2zM12 11v6M12 17l3-3M12 17l-3-3',
  lock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4',
  unlock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 019.9-1',
  // config: inner circle + outer gear ring (two paths)
  configInner: 'M12 15a3 3 0 100-6 3 3 0 000 6z',
  configOuter: 'M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
  trash: 'M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z',
  eyeInner: 'M12 15a3 3 0 100-6 3 3 0 000 6z',
  x: 'M18 6L6 18M6 6l12 12',
  check: 'M20 6L9 17l-5-5',
  arrow: 'M9 18l6-6-6-6',
}

function Icon({ d, size = 16, color }: { d: string; size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke={color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

/** Gear icon with inner circle + outer ring (two paths) */
function GearIcon({ size = 16, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke={color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={ic.configOuter} />
      <path d={ic.configInner} />
    </svg>
  )
}

// ─── useIsMobile hook ─────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  )
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < breakpoint)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [breakpoint])
  return mobile
}

function useIsCompact(breakpoint = 1050) {
  const [compact, setCompact] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  )
  useEffect(() => {
    const h = () => setCompact(window.innerWidth < breakpoint)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [breakpoint])
  return compact
}

// ─── Dialog Component ─────────────────────────────────
function V2Dialog({
                    open, onClose, title, children, footer,
                  }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode; footer: React.ReactNode
}) {
  if (!open) return null
  return (
    <div className="v2-dialog-overlay" onClick={onClose}>
      <div className="v2-dialog" onClick={e => e.stopPropagation()}>
        <div className="v2-dialog-header"><div className="v2-dialog-title">{title}</div></div>
        <div className="v2-dialog-body">{children}</div>
        <div className="v2-dialog-footer">{footer}</div>
      </div>
    </div>
  )
}

// ─── More Actions Dropdown (shared state) ─────────────
function MoreMenu({
                    id, actions, openMenuId, setOpenMenuId,
                  }: {
  id: string
  actions: { icon: string; label: string; danger?: boolean; onClick?: () => void }[]
  openMenuId: string | null
  setOpenMenuId: (id: string | null) => void
}) {
  const isOpen = openMenuId === id
  return (
    <div className="v2-more-menu">
      <button
        className={`v2-more-trigger ${isOpen ? 'open' : ''}`}
        onClick={(e) => { e.stopPropagation(); setOpenMenuId(isOpen ? null : id) }}
        type="button"
      >
        <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>
      {isOpen && (
        <>
          <div className="v2-more-backdrop" onClick={() => setOpenMenuId(null)} />
          <div className="v2-more-dropdown">
            {actions.map((a, i) => (
              <button
                key={i}
                className={`v2-more-item ${a.danger ? 'v2-more-item--danger' : ''}`}
                onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); a.onClick?.() }}
              >
                <Icon d={a.icon} size={14} />
                {a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Visibility Badge ─────────────────────────────────
function VisBadge({ isPublic }: { isPublic: boolean }) {
  return (
    <span className={`v2-badge ${isPublic ? 'v2-badge--public' : 'v2-badge--private'}`}>
      {isPublic ? 'Public' : 'Private'}
    </span>
  )
}

// ─── App Thumbnail Placeholder ────────────────────────
const THUMB_COLORS = [
  '#6950ff', '#e04080', '#16a34a', '#d97706', '#2563eb',
  '#8b5cf6', '#06b6d4', '#ea580c', '#4f46e5', '#0d9488',
]

function hashCode(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

function AppThumbnail({ title, path, size = 40 }: { title: string; path: string; size?: number }) {
  const color = THUMB_COLORS[hashCode(path) % THUMB_COLORS.length]
  const initials = (title || path)
    .split(/[\s\-_]+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')
  return (
    <div
      className="v2-app-thumb"
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: 'var(--v2-radius-md)',
        background: `${color}18`,
        border: `1px solid ${color}30`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.35,
        fontWeight: 700,
        color,
        letterSpacing: '-0.02em',
        flexShrink: 0,
      }}
    >
      {initials || '?'}
    </div>
  )
}

// ─── Main Export ───────────────────────────────────────
export function ProjectV2() {
  const { projectPath } = useParams<{ projectPath: string }>()
  const auth = useWistorAuth()
  const history = useHistory()

  useEffect(() => {
    if (auth.status !== 'authorized') history.push('/login', `projects/${projectPath}`)
  }, [auth])

  if (auth.status !== 'authorized') return null
  return <ProjectContent projectPath={projectPath} />
}

// ─── Project Content ──────────────────────────────────
function ProjectContent({ projectPath }: { projectPath: string }) {
  const auth = useWistorAuth()
  const history = useHistory()
  const mobile = useIsMobile()
  const compact = useIsCompact()
  const projectQuery = useProject(projectPath)
  const projectsQuery = useProjects()
  const renameApp = useRenameApp()
  const moveApp = useMoveApp()
  const togglePublic = useToggleAppPublic()
  const deleteApp = useDeleteApp()

  const [search, setSearch] = useState('')
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [renameDialog, setRenameDialog] = useState<{ id: string; title: string } | null>(null)
  const [moveDialog, setMoveDialog] = useState<{ id: string; title: string } | null>(null)
  const [moveTargetId, setMoveTargetId] = useState('')
  const [deleteDialog, setDeleteDialog] = useState<{ id: string; title: string } | null>(null)
  const [configPanel, setConfigPanel] = useState<string | null>(null) // stores app.id
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkMoveDialog, setBulkMoveDialog] = useState(false)
  const [bulkMoveTargetId, setBulkMoveTargetId] = useState('')

  const project = projectQuery.data
  const isSuperadmin = auth.status === 'authorized' && auth.user.role === 'superadmin'
  const canEdit = auth.status === 'authorized' && ['superadmin', 'admin', 'editor'].includes(auth.user.role)

  const viewers = useMemo(() => {
    if (!project?.viewers) return []
    let items = orderBy(project.viewers, ['title'], ['asc']) as AppItem[]
    items = items.filter(v => !v.deleted)
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(v =>
        v.title?.toLowerCase().includes(q) || v.path?.toLowerCase().includes(q)
      )
    }
    return items
  }, [project, search])

  const otherProjects = useMemo(() => {
    if (!projectsQuery.data) return []
    return projectsQuery.data.filter((p: any) => p.path !== projectPath)
  }, [projectsQuery.data, projectPath])

  // Navigate to the app editor
  const openApp = useCallback((app: AppItem) => {
    history.push(`/projects/${projectPath}/${app.path}`)
  }, [history, projectPath])

  // Handlers
  const handleRename = () => {
    if (!renameDialog?.title.trim()) return
    renameApp.mutate(
      { id: renameDialog.id, title: renameDialog.title.trim() },
      { onSuccess: () => setRenameDialog(null), onError: () => alert('Failed to rename app.') }
    )
  }

  const handleMove = () => {
    if (!moveDialog || !moveTargetId) return
    moveApp.mutate(
      { id: moveDialog.id, targetProjectId: moveTargetId },
      { onSuccess: () => { setMoveDialog(null); setMoveTargetId('') }, onError: () => alert('Failed to move app.') }
    )
  }

  const handleDelete = () => {
    if (!deleteDialog) return
    deleteApp.mutate(deleteDialog.id, {
      onSuccess: () => setDeleteDialog(null),
      onError: () => alert('Failed to delete app.'),
    })
  }

  const handleTogglePublic = (app: AppItem) => {
    togglePublic.mutate({ id: app.id, isPublic: !app.public })
  }

  const newClicked = () => {
    const id = Array.from({ length: 5 }, () =>
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
        .charAt(Math.floor(Math.random() * 62))
    ).join('')
    history.push(`/projects/${projectPath}/${id}/edit`)
  }

  // Build action list for a given app
  // Look up live app data from the refreshed viewers list (for config panel)
  const configPanelApp = useMemo(() => {
    if (!configPanel) return null
    return viewers.find(v => v.id === configPanel) ?? null
  }, [configPanel, viewers])

  // Selection helpers
  const toggleSelect = (id: string) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }
  const allSelected = viewers.length > 0 && viewers.every(v => selected.has(v.id))
  const toggleAll = () => allSelected ? setSelected(new Set()) : setSelected(new Set(viewers.map(v => v.id)))
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()) }

  const handleBulkMove = () => {
    if (!bulkMoveTargetId || selected.size === 0) return
    Promise.all(
      Array.from(selected).map(id =>
        moveApp.mutateAsync({ id, targetProjectId: bulkMoveTargetId })
      )
    ).then(() => {
      setBulkMoveDialog(false)
      setBulkMoveTargetId('')
      exitSelectMode()
    }).catch(() => alert('Failed to move some apps.'))
  }

  const appActions = useCallback((app: AppItem) => {
    const actions: { icon: string; label: string; danger?: boolean; onClick?: () => void }[] = []
    if (canEdit) {
      actions.push({ icon: ic.edit, label: 'Rename', onClick: () => setRenameDialog({ id: app.id, title: app.title }) })
      actions.push({ icon: app.public ? ic.lock : ic.unlock, label: app.public ? 'Make Private' : 'Make Public', onClick: () => handleTogglePublic(app) })
    }
    if (isSuperadmin) {
      actions.push({ icon: ic.move, label: 'Move', onClick: () => setMoveDialog({ id: app.id, title: app.title }) })
    }
    if (isSuperadmin) {
      actions.push({ icon: ic.trash, label: 'Delete', danger: true, onClick: () => setDeleteDialog({ id: app.id, title: app.title }) })
    }
    return actions
  }, [canEdit, isSuperadmin])

  if (projectQuery.isLoading) return <div className="v2-spinner" />
  if (!project) return <div className="v2-empty"><div className="v2-empty-text">Project not found.</div></div>

  return (
    <div className="v2-page">
      {/* Breadcrumb */}
      <div className="v2-breadcrumb">
        <button onClick={() => history.push('/v2/home')}>Projects</button>
        <Icon d={ic.arrow} size={12} />
        <span className="v2-breadcrumb-current">{project.title}</span>
      </div>

      {/* Header */}
      <div className="v2-page-header">
        <div>
          <h1 className="v2-page-title">{project.title}</h1>
          <div className="v2-path">/{project.path}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isSuperadmin && !selectMode && (
            <button className="v2-btn v2-btn--secondary" onClick={() => setSelectMode(true)}>
              Select
            </button>
          )}
          {selectMode && (
            <button className="v2-btn v2-btn--ghost" onClick={exitSelectMode}>
              Cancel
            </button>
          )}
          {canEdit && (
            <button className="v2-btn v2-btn--primary" onClick={newClicked}>
              <Icon d={ic.plus} size={14} />
              New App
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectMode && selected.size > 0 && (
        <div className="v2-bulk-bar">
          <span className="v2-bulk-count">{selected.size} selected</span>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button className="v2-btn v2-btn--secondary v2-btn--sm" onClick={() => setBulkMoveDialog(true)}>
              <Icon d={ic.move} size={13} />
              Move Selected
            </button>
            <button className="v2-btn v2-btn--ghost v2-btn--sm" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="v2-search-wrap">
          <span className="v2-search-icon"><Icon d={ic.search} size={15} /></span>
          <input
            className="v2-input"
            placeholder="Search apps..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 38 }}
          />
        </div>
        <span style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>{viewers.length} apps</span>
      </div>

      {/* ─── Mobile: card list ─── */}
      {mobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {viewers.map(app => (
            <div key={app.id} className={`v2-mobile-card ${selectMode && selected.has(app.id) ? 'selected' : ''}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                {selectMode && (
                  <input
                    type="checkbox"
                    className="v2-checkbox"
                    style={{ marginTop: 4, marginRight: 10 }}
                    checked={selected.has(app.id)}
                    onChange={() => toggleSelect(app.id)}
                  />
                )}
                <div
                  style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0, cursor: 'pointer', flex: 1 }}
                  onClick={() => openApp(app)}
                >
                  <AppThumbnail title={app.title} path={app.path} size={38} />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14, fontWeight: 600, color: 'var(--v2-text-primary)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                      title={app.title}
                    >
                      {app.title}
                    </div>
                    <div className="v2-path">/{app.path}</div>
                  </div>
                </div>
                <VisBadge isPublic={app.public} />
              </div>
              <div className="v2-mobile-card-footer">
                <span style={{ fontSize: 12, color: 'var(--v2-text-tertiary)' }}>
                  {app.user?.name ?? '—'} · {app.createdAt ? new Date(app.createdAt).toLocaleDateString() : ''}
                </span>
                {canEdit && (
                  <div style={{ display: 'flex', gap: 2 }}>
                    <button className="v2-action v2-action--icon-only" onClick={() => setConfigPanel(app.id)} title="Config">
                      <GearIcon size={14} />
                    </button>
                    <MoreMenu
                      id={`m-${app.id}`}
                      openMenuId={openMenuId}
                      setOpenMenuId={setOpenMenuId}
                      actions={appActions(app)}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ─── Desktop: scrollable table ─── */
        <div className="v2-table-wrap">
          <div className="v2-table-scroll">
            <table className="v2-table" style={{ minWidth: compact ? 600 : 750, tableLayout: 'fixed' }}>
              <thead>
              <tr>
                {selectMode && (
                  <th style={{ width: 40 }}>
                    <input type="checkbox" className="v2-checkbox" checked={allSelected} onChange={toggleAll} />
                  </th>
                )}
                <th style={{ width: 56 }}></th>
                <th style={{ width: canEdit ? (selectMode ? '28%' : '30%') : '35%' }}>App</th>
                <th style={{ width: canEdit ? (compact ? '15%' : '18%') : '20%' }}>Path</th>
                <th style={{ width: canEdit ? '10%' : '13%' }}>Visibility</th>
                <th style={{ width: canEdit ? '12%' : '16%' }}>Creator</th>
                <th style={{ width: canEdit ? '10%' : '14%' }}>Created</th>
                {canEdit && (
                  <th className="actions" style={{ textAlign: 'right', width: compact ? '18%' : '15%' }}>Actions</th>
                )}
              </tr>
              </thead>
              <tbody>
              {viewers.map(app => (
                <tr key={app.id} className={selectMode && selected.has(app.id) ? 'selected' : ''}>
                  {selectMode && (
                    <td style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        className="v2-checkbox"
                        checked={selected.has(app.id)}
                        onChange={() => toggleSelect(app.id)}
                      />
                    </td>
                  )}
                  <td style={{ width: 56, paddingRight: 0 }}>
                    <AppThumbnail title={app.title} path={app.path} size={36} />
                  </td>
                  <td className="primary">
                      <span
                        style={{
                          cursor: 'pointer',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={app.title}
                        onClick={() => openApp(app)}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--v2-accent)' }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '' }}
                      >
                        {app.title}
                      </span>
                  </td>
                  <td className="mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`/${app.path}`}>/{app.path}</td>
                  <td><VisBadge isPublic={app.public} /></td>
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.user?.name ?? '—'}</td>
                  <td style={{ color: 'var(--v2-text-tertiary)', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {app.createdAt ? new Date(app.createdAt).toLocaleDateString() : ''}
                  </td>
                  {canEdit && (
                    <td className="actions" style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        {canEdit && (
                          <button
                            className="v2-action v2-action--icon-only"
                            onClick={() => setRenameDialog({ id: app.id, title: app.title })}
                            title="Rename"
                          >
                            <Icon d={ic.edit} size={14} />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            className="v2-action v2-action--icon-only"
                            onClick={() => handleTogglePublic(app)}
                            title={app.public ? 'Make Private' : 'Make Public'}
                          >
                            <Icon d={app.public ? ic.lock : ic.unlock} size={14} />
                          </button>
                        )}
                        {isSuperadmin && (
                          <button
                            className="v2-action v2-action--icon-only"
                            onClick={() => setMoveDialog({ id: app.id, title: app.title })}
                            title="Move"
                          >
                            <Icon d={ic.move} size={14} />
                          </button>
                        )}
                        <button
                          className="v2-action v2-action--icon-only"
                          onClick={() => setConfigPanel(app.id)}
                          title="Config"
                        >
                          <GearIcon size={14} />
                        </button>
                        {isSuperadmin && (
                          <button
                            className="v2-action v2-action--icon-only v2-action--danger"
                            onClick={() => setDeleteDialog({ id: app.id, title: app.title })}
                            title="Delete"
                          >
                            <Icon d={ic.trash} size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewers.length === 0 && (
        <div className="v2-empty">
          <div className="v2-empty-text">{search ? 'No apps match your search.' : 'No apps in this project yet.'}</div>
        </div>
      )}

      {/* ─── Rename Dialog ─── */}
      <V2Dialog
        open={!!renameDialog}
        onClose={() => setRenameDialog(null)}
        title="Rename App"
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => setRenameDialog(null)}>Cancel</button>
          <button className="v2-btn v2-btn--primary" onClick={handleRename} disabled={renameApp.isPending}>
            {renameApp.isPending ? 'Saving...' : 'Save'}
          </button>
        </>}
      >
        {renameDialog && (
          <div className="v2-field">
            <label className="v2-label">Title</label>
            <input className="v2-input" value={renameDialog.title}
                   onChange={e => setRenameDialog(d => d ? { ...d, title: e.target.value } : d)} autoFocus />
          </div>
        )}
      </V2Dialog>

      {/* ─── Move Dialog ─── */}
      <V2Dialog
        open={!!moveDialog}
        onClose={() => { setMoveDialog(null); setMoveTargetId('') }}
        title={moveDialog ? `Move "${moveDialog.title}"` : 'Move App'}
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => { setMoveDialog(null); setMoveTargetId('') }}>Cancel</button>
          <button className="v2-btn v2-btn--primary" onClick={handleMove} disabled={!moveTargetId || moveApp.isPending}>
            {moveApp.isPending ? 'Moving...' : 'Move'}
          </button>
        </>}
      >
        <div className="v2-field">
          <label className="v2-label">Target Project</label>
          <select className="v2-select" value={moveTargetId} onChange={e => setMoveTargetId(e.target.value)}>
            <option value="">Select a project...</option>
            {otherProjects.map((p: any) => (
              <option key={p.id || p._id} value={p.id || p._id}>{p.title}</option>
            ))}
          </select>
        </div>
      </V2Dialog>

      {/* ─── Delete Dialog ─── */}
      <V2Dialog
        open={!!deleteDialog}
        onClose={() => setDeleteDialog(null)}
        title="Delete App"
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => setDeleteDialog(null)}>Cancel</button>
          <button className="v2-btn v2-btn--danger" onClick={handleDelete} disabled={deleteApp.isPending}>
            {deleteApp.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </>}
      >
        {deleteDialog && (
          <p style={{ fontSize: 14, color: 'var(--v2-text-secondary)', lineHeight: 1.6 }}>
            Are you sure you want to delete <strong>"{deleteDialog.title}"</strong>?
            This is a soft delete — the app can be recovered later.
          </p>
        )}
      </V2Dialog>

      {/* ─── Bulk Move Dialog ─── */}
      <V2Dialog
        open={bulkMoveDialog}
        onClose={() => { setBulkMoveDialog(false); setBulkMoveTargetId('') }}
        title={`Move ${selected.size} App${selected.size !== 1 ? 's' : ''}`}
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => { setBulkMoveDialog(false); setBulkMoveTargetId('') }}>Cancel</button>
          <button className="v2-btn v2-btn--primary" onClick={handleBulkMove} disabled={!bulkMoveTargetId || moveApp.isPending}>
            {moveApp.isPending ? 'Moving...' : 'Move'}
          </button>
        </>}
      >
        <p style={{ fontSize: 14, color: 'var(--v2-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
          Move <strong>{selected.size} selected app{selected.size !== 1 ? 's' : ''}</strong> to another project.
          Apps with conflicting paths in the target project will fail individually.
        </p>
        <div className="v2-field">
          <label className="v2-label">Target Project</label>
          <select className="v2-select" value={bulkMoveTargetId} onChange={e => setBulkMoveTargetId(e.target.value)}>
            <option value="">Select a project...</option>
            {otherProjects.map((p: any) => (
              <option key={p.id || p._id} value={p.id || p._id}>{p.title}</option>
            ))}
          </select>
        </div>
      </V2Dialog>

      {/* ─── Config Panel ─── */}
      {configPanelApp && (
        <AppConfigPanel
          app={configPanelApp}
          projectPath={projectPath}
          onClose={() => setConfigPanel(null)}
          canEdit={canEdit}
        />
      )}
    </div>
  )
}

// ─── App Config Side Panel ────────────────────────────
function AppConfigPanel({
                          app, projectPath, onClose, canEdit,
                        }: {
  app: AppItem; projectPath: string; onClose: () => void; canEdit: boolean
}) {
  const api = useApiClient()
  const { baseUrl } = useApiBase()
  const qc = useQueryClient()

  // Fetch the full viewer config
  const configQuery = useQuery({
    queryKey: ['v2', baseUrl, 'app-config', projectPath, app.path],
    queryFn: async () => {
      const { data } = await api.get(`/api/projects/${projectPath}/viewers/${app.path}`)
      return data
    },
  })

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saved, setSaved] = useState(false)

  // When config loads, set draft
  useEffect(() => {
    if (configQuery.data) {
      setDraft(JSON.stringify(configQuery.data, null, 2))
    }
  }, [configQuery.data])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (configJson: string) => {
      const parsed = JSON.parse(configJson)
      // Strip fields that are populated references or internal — sending
      // these back would overwrite ObjectId refs with nested objects and
      // corrupt the document.
      const { _id, id, __v, user, project, createdAt, ...safeFields } = parsed
      const { data } = await api.put(
        `/api/projects/${projectPath}/viewers/${app.path}`,
        safeFields,
      )
      return data
    },
    onSuccess: () => {
      setEditing(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
      qc.invalidateQueries({ queryKey: ['v2', baseUrl, 'app-config', projectPath, app.path] })
      // Also refresh the project table so updated app info is reflected
      qc.invalidateQueries({ queryKey: ['v2', baseUrl, 'project', projectPath] })
    },
    onError: (err: any) => alert('Failed to save: ' + (err?.message || 'Unknown error')),
  })

  const handleSave = () => {
    try {
      JSON.parse(draft) // validate
    } catch (e: any) {
      alert('Invalid JSON: ' + e.message)
      return
    }
    saveMutation.mutate(draft)
  }

  const handleCancel = () => {
    setDraft(configQuery.data ? JSON.stringify(configQuery.data, null, 2) : '')
    setEditing(false)
  }

  return (
    <>
      <div className="v2-panel-backdrop" onClick={onClose} />
      <div className="v2-panel">
        {/* Header */}
        <div className="v2-panel-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="v2-panel-title">Configuration</span>
            {saved && (
              <span className="v2-saved-indicator">
                <Icon d={ic.check} size={14} color="var(--v2-success)" /> Saved
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {canEdit && !editing && !configQuery.isLoading && (
              <button className="v2-btn v2-btn--edit v2-btn--sm" onClick={() => setEditing(true)}>
                <Icon d={ic.edit} size={12} /> Edit
              </button>
            )}
            <button className="v2-panel-close" onClick={onClose}>
              <Icon d={ic.x} size={18} />
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="v2-panel-summary">
          <div className="v2-panel-summary-grid">
            <div>
              <div className="v2-panel-summary-label">Title</div>
              <div className="v2-panel-summary-value">{app.title}</div>
            </div>
            <div>
              <div className="v2-panel-summary-label">Path</div>
              <div className="v2-panel-summary-value v2-mono" style={{ fontSize: 12 }}>/{app.path}</div>
            </div>
            <div>
              <div className="v2-panel-summary-label">Visibility</div>
              <div className="v2-panel-summary-value"><VisBadge isPublic={app.public} /></div>
            </div>
            <div>
              <div className="v2-panel-summary-label">Creator</div>
              <div className="v2-panel-summary-value">{app.user?.name ?? 'Unknown'}</div>
            </div>
          </div>
        </div>

        {/* Editor body */}
        <div className="v2-panel-body">
          {configQuery.isLoading ? (
            <div className="v2-spinner" />
          ) : configQuery.isError ? (
            <div style={{ color: 'var(--v2-danger)', fontSize: 14, padding: 20 }}>
              Failed to load config.
            </div>
          ) : editing ? (
            <textarea
              className="v2-code-editor"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              spellCheck={false}
            />
          ) : (
            <pre className="v2-code-block">
              {configQuery.data != null
                ? JSON.stringify(configQuery.data, null, 2)
                : '{}'}
            </pre>
          )}
        </div>

        {/* Footer (edit mode only) */}
        {editing && (
          <div className="v2-panel-footer">
            <button className="v2-btn v2-btn--ghost v2-btn--sm" onClick={handleCancel}>Cancel</button>
            <button className="v2-btn v2-btn--primary v2-btn--sm" onClick={handleSave} disabled={saveMutation.isPending}>
              <Icon d={ic.check} size={13} color="#fff" />
              {saveMutation.isPending ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        )}
      </div>
    </>
  )
}